import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Formik, Form, FormikHelpers, FormikErrors } from 'formik';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { SelectOption } from '../../../../components/Select/interface';
import Select from '../../../../components/Select';
import { LocationSelectTypes, RewardBonusesData } from '../../../../redux/slices/rewardBonusesSlice';
import FilePickerInput from '../../../../components/FilePickerInput';
import {
  RewardPartnerCompany,
  selectRewardPartnerCompany
} from '../../../../redux/slices/rewardPartnerCompanySlice';

interface RewardBonusesModalProps {
  onSave: (
    values: RewardBonusesData,
    bgPhoto?: string | File,
    isEditMode?: boolean
  ) => void;
}

const defaultInitialData: RewardBonusesData = {
  partnerCompany: '',
  name: '',
  description: '',
  description2: '',
  bgPhoto: '',
  instagram: '',
  website: '',
  locations: [
    {
      type: "coords",
      coords: {
        lat: 0,
        lon: 0
      },
      mapUrl: ""
    }
  ],
  activateLimit: 0,
  bonusPointsPrice: 0,
  enabled: false
};

export interface RewardBonusesModalHandles {
  openAddModal: () => void;
  openEditModal: (initialData: RewardBonusesData) => void;
}

const validationSchema = yup.object({
  partnerCompany: yup.string().required('Партнерська компанія є обов’язковою'),
  name: yup.string().required('Назва є обов’язковою'),
  description: yup.string().required('Опис є обов’язковим'),
  description2: yup.string().notRequired(),
  instagram: yup
    .string()
    .url('Некоректне посилання на Instagram')
    .notRequired(),
  website: yup.string().url('Некоректне посилання на сайт').notRequired(),
  locations: yup
    .array()
    .of(
      yup.object().shape({
        type: yup
          .mixed<LocationSelectTypes>()
          .oneOf(['mapUrl', 'coords']).default("coords"),
        coords: yup
          .object({
            lat: yup
              .number()
              .min(-90, 'Широта має бути від -90 до 90')
              .max(90, 'Широта має бути від -90 до 90'),
            lon: yup
              .number()
              .min(-180, 'Довгота має бути від -180 до 180')
              .max(180, 'Довгота має бути від -180 до 180')
          })
          .notRequired(),
        mapUrl: yup.string().url('Некоректне посилання на карту').notRequired()
      })
    )
    .test(
      'at-least-one-type',
      'Кожен запис має містити або координати, або посилання',
      (locations) =>
        Array.isArray(locations) &&
        locations.every(loc => loc.coords || loc.mapUrl)
    ),
  activateLimit: yup
    .number()
    .required('Ліміт активацій є обов’язковим')
    .min(0, 'Ліміт активацій не може бути від’ємним'),
  bonusPointsPrice: yup
    .number()
    .required('Ціна бонусних балів є обов’язковою')
    .min(0, 'Ціна бонусних балів не може бути від’ємною'),
  enabled: yup.boolean().notRequired()
});

const RewardBonusesModal = forwardRef<
  RewardBonusesModalHandles,
  RewardBonusesModalProps
>(({ onSave }, ref) => {
  const { items: rewardPartnerCompanies } = useSelector(
    selectRewardPartnerCompany
  );

  const [open, setOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [initialData, setInitialData] =
    useState<RewardBonusesData>(defaultInitialData);

  const [bgPhoto, setBgPhoto] = useState<File | undefined>();

  const rewardPartnerCompaniesOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: '-' },
      ...rewardPartnerCompanies.map(rewardPartnerCompany => ({
        label: rewardPartnerCompany.name,
        value: rewardPartnerCompany._id
      }))
    ],
    [rewardPartnerCompanies]
  );

  useEffect(() => {
    if (!open) {
      setInitialData(defaultInitialData);
      setBgPhoto(undefined);
    }
  }, [open]);

  useImperativeHandle(ref, () => ({
    openAddModal() {
      setIsEditMode(false);
      setInitialData(defaultInitialData);
      setOpen(true);
    },
    openEditModal(data: RewardBonusesData) {
      setIsEditMode(true);
      const bonusData = { ...data };
      bonusData.partnerCompany = (
        data.partnerCompany as RewardPartnerCompany
      )?._id;
      setInitialData(bonusData);
      setOpen(true);
    }
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (
    values: RewardBonusesData,
    helpers: FormikHelpers<RewardBonusesData>
  ) => {
    onSave(values, bgPhoto, isEditMode);
    setOpen(false);
    helpers.setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {isEditMode ? 'Редагувати бонус' : 'Додати бонус'}
      </DialogTitle>
      <Formik
        initialValues={initialData}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          touched,
          errors
        }) => (
          <Form>
            <DialogContent>
              <Select
                fullWidth
                label='Партнерська компанія'
                options={rewardPartnerCompaniesOptions}
                value={values.partnerCompany as string}
                onChange={value => setFieldValue('partnerCompany', value)}
                error={Boolean(touched.partnerCompany && errors.partnerCompany)}
                helperText={
                  touched.partnerCompany && errors.partnerCompany
                    ? errors.partnerCompany
                    : undefined
                }
              />
              <TextField
                margin='dense'
                label='Назва'
                name='name'
                fullWidth
                variant='standard'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={errors.name}
              />
              <TextField
                margin='dense'
                label='Опис'
                name='description'
                fullWidth
                variant='standard'
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.description && errors.description)}
                helperText={errors.description}
              />
              <TextField
                margin='dense'
                label='Опис 2'
                name='description2'
                fullWidth
                variant='standard'
                value={values.description2}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.description2 && errors.description2)}
                helperText={errors.description2}
              />
              <TextField
                margin='dense'
                label='Instagram'
                name='instagram'
                fullWidth
                variant='standard'
                value={values.instagram}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.instagram && errors.instagram)}
                helperText={errors.instagram}
              />
              <TextField
                margin='dense'
                label='Сайт'
                name='website'
                fullWidth
                variant='standard'
                value={values.website}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.website && errors.website)}
                helperText={errors.website}
              />
              <TextField
                margin='dense'
                label='Ціна бонусних балів'
                type='number'
                name='bonusPointsPrice'
                fullWidth
                variant='standard'
                value={values.bonusPointsPrice}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.bonusPointsPrice && errors.bonusPointsPrice
                )}
                helperText={errors.bonusPointsPrice}
              />
              <TextField
                margin='dense'
                label='Ліміт активацій'
                type='number'
                name='activateLimit'
                fullWidth
                variant='standard'
                value={values.activateLimit}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.activateLimit && errors.activateLimit)}
                helperText={errors.activateLimit}
              />
              {values.locations.map((location, index) => {
                const locationError =
                  typeof errors.locations?.[index] === 'object'
                    ? (errors.locations?.[index] as FormikErrors<typeof values.locations[0]>)
                    : undefined;

                if (!location.type) {
                  setFieldValue(`locations[${index}].type`, location.mapUrl?.length ? 'mapUrl' : 'coords')
                }

                return (
                  <div key={index} style={{ border: '1px solid #ccc', padding: 12, marginBottom: 16 }}>
                    <Select
                      fullWidth
                      label='Тип'
                      options={[
                        {
                          label: "Посилання",
                          value: "mapUrl"
                        },
                        {
                          label: "Координати",
                          value: "coords"
                        }
                      ]}
                      onChange={value => {
                        setFieldValue(`locations[${index}].type`, value)

                        if (value === "mapUrl") {
                          setFieldValue(`locations[${index}].coords.lat`, 0)
                          setFieldValue(`locations[${index}].coords.lon`, 0)
                        }

                        if (value === "coords") {
                          setFieldValue(`locations[${index}].mapUrlt`, "")
                        }
                      }}
                      value={location.type}
                    />

                    {
                      location.type === 'coords' ? (
                        <>
                          <TextField
                            label='Широта'
                            type='number'
                            fullWidth
                            variant='standard'
                            value={location.coords?.lat ?? ''}
                            onChange={(e) =>
                              setFieldValue(`locations[${index}].coords.lat`, Number(e.target.value))
                            }
                            margin='dense'
                          />
                          <TextField
                            label='Довгота'
                            type='number'
                            fullWidth
                            variant='standard'
                            value={location.coords?.lon ?? ''}
                            onChange={(e) =>
                              setFieldValue(`locations[${index}].coords.lon`, Number(e.target.value))
                            }
                            margin='dense'
                          />
                        </>
                      ) : null
                    }

                    {
                      location.type === "mapUrl" ? (
                        <TextField
                          label='Map URL'
                          fullWidth
                          variant='standard'
                          value={location.mapUrl ?? ''}
                          onChange={(e) => setFieldValue(`locations[${index}].mapUrl`, e.target.value)}
                          margin='dense'
                          error={Boolean(touched.locations?.[index]?.mapUrl && locationError?.mapUrl)}
                          helperText={
                            touched.locations?.[index]?.mapUrl && locationError?.mapUrl
                              ? locationError.mapUrl
                              : ''
                          }
                        />
                      ) : null
                    }
                    {typeof errors.locations?.[index] === 'string' ? (
                      <div style={{ color: 'red' }}>{errors.locations[index]}</div>
                    ) : null}
                    <Button
                      onClick={() => {
                        const updated = [...values.locations];
                        updated.splice(index, 1);
                        setFieldValue('locations', updated);
                      }}
                      color='error'
                    >
                      Видалити
                    </Button>
                  </div>
                );
              })}

              {
                values?.locations?.length < 1 ? (
                  <Button
                    onClick={() => {
                      setFieldValue('locations', [
                        ...values.locations,
                        {
                          coords: { lat: 0, lon: 0 }
                        }
                      ]);
                    }}
                  >
                    ➕ Додати локацію
                  </Button>
                ) : null
              }

              <FilePickerInput
                label='Фонове зображення'
                value={bgPhoto || undefined}
                onChange={(file: File) => {
                  setBgPhoto(file);
                }}
              />
              {isEditMode ? (
                <FormGroup sx={{ marginLeft: 'auto' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.enabled}
                        onChange={event =>
                          setFieldValue('enabled', event.target.checked)
                        }
                      />
                    }
                    label='Активовано'
                  />
                </FormGroup>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Скасувати</Button>
              <Button type='submit' color='primary' disabled={isSubmitting}>
                Зберегти
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});

export default RewardBonusesModal;
